import React from 'react';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import Icon from '../icon';
import styled from 'styled-components/native';
import TextGeneric from '../textGeneric';
import MyPressable from '../myPressable';
import {I18nManager, Platform} from 'react-native';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

const Container = styled(MyPressable)`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 16px;
  padding-vertical: 12px;
  margin-bottom: 6px;
  border-radius: 10px;
  background-color: ${({theme}) => (theme as Theme).screenBackground};
`;

const WebContainer = styled(Container)`
  padding-horizontal: 20px;
  padding-vertical: 16px;
  margin-bottom: 10px;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid ${({theme}) => (theme as Theme).border + '20'};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: ${({theme}) => (theme as Theme).primary + '40'};
  }
`;

const MobileWebContainer = styled(Container)`
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({theme}) => (theme as Theme).border + '15'};
`;

const LeftContainer = styled.View`
  flex-direction: row;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
`;

const IconContainer = styled.View`
  background-color: ${({theme}) => (theme as Theme).settings.iconBackground};
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin-right: 12px;
  min-width: 40px;
  min-height: 40px;
`;

const WebIconContainer = styled(IconContainer)`
  padding: 12px;
  border-radius: 12px;
  margin-right: 16px;
  min-width: 48px;
  min-height: 48px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const ContentContainer = styled.View`
  flex: 1;
  justify-content: center;
`;

const Title = styled(TextGeneric)`
  font-size: 14px;
  font-weight: 500;
  color: ${({theme}) => (theme as Theme).text.dark};
  margin-bottom: 2px;
  line-height: 18px;
`;

const WebTitle = styled(Title)`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 20px;
  transition: color 0.3s ease-in-out;
`;

const Subtitle = styled(TextGeneric)`
  font-size: 11px;
  color: ${({theme}) => (theme as Theme).text.grey};
  line-height: 14px;
`;

const WebSubtitle = styled(Subtitle)`
  font-size: 13px;
  line-height: 16px;
`;

const ArrowContainer = styled.View`
  align-self: center;
  justify-content: center;
  padding-left: 8px;
  transform: ${I18nManager.isRTL ? 'rotate(180deg)' : 'rotate(0deg)'};
  min-width: 20px;
  min-height: 20px;
`;

const WebArrowContainer = styled(ArrowContainer)`
  padding-left: 12px;
  min-width: 24px;
  min-height: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: ${I18nManager.isRTL ? 'rotate(180deg) scale(1.2)' : 'rotate(0deg) scale(1.2)'};
  }
`;

export interface SupportItemProps {
  title: string;
  subtitle?: string;
  iconName: string;
  iconColor?: string;
  iconBackgroundColor?: string;
  onPress?: () => void;
  style?: any;
}

/**
 * A dedicated Support Item component with enhanced responsive design
 * and modern styling for both web and mobile platforms
 */
const SupportItem: React.FC<SupportItemProps> = ({
  title,
  subtitle,
  iconName,
  iconColor,
  iconBackgroundColor,
  onPress,
  style,
}: SupportItemProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {isMobile, isLargeScreen} = useResponsiveDimensions();
  const isWeb = Platform.OS === 'web';

  const defaultIconColor = iconColor || theme.primary;
  
  // Responsive icon sizing based on screen size
  const getIconSize = () => {
    if (isWeb && isLargeScreen) return 22;
    if (isWeb && !isMobile) return 20;
    if (isMobile) return 18;
    return 20; // default
  };

  const getArrowSize = () => {
    if (isWeb && isLargeScreen) return 18;
    if (isWeb && !isMobile) return 16;
    if (isMobile) return 14;
    return 16; // default
  };

  // Choose the appropriate styled components based on platform
  const ContainerComponent = isWeb 
    ? (isMobile ? MobileWebContainer : WebContainer)
    : Container;
  
  const IconContainerComponent = isWeb ? WebIconContainer : IconContainer;
  const TitleComponent = isWeb && !isMobile ? WebTitle : Title;
  const SubtitleComponent = isWeb ? WebSubtitle : Subtitle;
  const ArrowContainerComponent = isWeb ? WebArrowContainer : ArrowContainer;

  return (
    <ContainerComponent
      onPress={onPress}
      containerStyle={style}>
      <LeftContainer>
        <IconContainerComponent 
          style={{
            backgroundColor: iconBackgroundColor || theme.settings.iconBackground
          }}>
          <Icon
            name={iconName}
            size={getIconSize()}
            color={defaultIconColor}
          />
        </IconContainerComponent>
        <ContentContainer>
          <TitleComponent>
            {title}
          </TitleComponent>
          {subtitle && (
            <SubtitleComponent>
              {subtitle}
            </SubtitleComponent>
          )}
        </ContentContainer>
      </LeftContainer>
      <ArrowContainerComponent>
        <Icon
          name={'arrowRight'}
          size={getArrowSize()}
          color={theme.text.grey}
        />
      </ArrowContainerComponent>
    </ContainerComponent>
  );
};

export default SupportItem;
